import { forwardRef, useImperativeHandle } from 'react';
import { Tree, Dropdown, Input, Menu, Modal, Spin } from 'antd';
import SvgIcon from '&/components/SvgIcon';
import { getTargetElement } from '&/utils/common';
import { ExclamationCircleOutlined } from '@ant-design/icons';
import { createUUID } from '&/utils/common';
import { useEventEmitter } from '&/utils/customHooks/useEventEmitter';
import { deepCopy } from '&/utils/common';
import { getTreeTargetData } from '@/utils/common';
import useStoreState from '&/utils/customHooks/useStoreState';
import './index.scss';
import './DetailLeftTree.scss';

const DetailLeftTree = forwardRef((props, ref) => {
    const {
        treeMenuData,
        menuOpt = {},
        initComponent,
        hasInterceptBeforeChangeNode,
        getResetTreeKey,
        updateType,
        defaultExpandedKeys,
        multiple = true,
        onTreeSelect, // 父组件状态同步选择
    } = props;
    const {
        menuTitle,
        showTitleAdd,
        titleAdd,
        nodeMenu,
        customTitleMenu,
        saveNode,
        updateNode,
        removeNode,
        beforeChangeNode,
        beforeAddNode,
        afterAddNode,
        inputMaxLength,
        verifyValue,
        verifySaveValue,
        updateTreeData,
    } = menuOpt;

    const wrap = useRef();
    const selectFlag = useRef(true);
    const currentNode = useRef();
    const newNodeKey = useRef();
    const handler = useRef();

    const [selectedData, setSelectedData] = useState({});
    const [selectedKeys, setSelectedKeys] = useState([]);
    const [treeData, setTreeData] = useState([]);
    const [loading, setLoading] = useState(false);
    const [expandedKeys, setExpandedKeys] = useState([]);
    const [newNode, setNewNode] = useState(null);
    const { emit } = useEventEmitter();
    const findItem = useRef();

    useEffect(() => {
        if (findItem.current) {
            let findedStage = null;
            for (const template of treeData) {
                if (template.id === findItem.current.templateId) {
                    findedStage = template.children.find(
                        stage => stage.id === findItem.current.templateStageId
                    );
                    break;
                }
            }
            findedStage && onSelect([findedStage.key], { node: findedStage });
            findItem.current = null;
        }
    }, [treeData]);

    useImperativeHandle(ref, () => {
        return {
            onAdd,
            handleMore,
            titleAddHandle,
            setTreeData,
            setSelectedKeys,
            onSelect,
            setExpandedKeys,
            findAndSelectNode(item) {
                findItem.current = item;
            },
        };
    });

    useEffect(() => {
        if (newNode) {
            onEdit(newNode.key, newNode.name);
        }
    }, [newNode]);

    useEffect(() => {
        initTreeData();
    }, [treeMenuData, initComponent]);

    const [autoChangeSelectedKeys, changeAutoChangeSelectedKeys] =
        useState(false);

    useEffect(() => {
        if (treeMenuData.length > 0) {
            if (selectedKeys.length === 0 || autoChangeSelectedKeys) {
                treeDataChange();
            }
        }
        updateTreeData?.(treeData, newNode);
    }, [treeData]);

    React.useLayoutEffect(() => {
        if (needView.current) {
            setTimeout(selectedIntoView, 800);
            needView.current = false;
        }
    }, [treeData]);

    useEffect(() => {
        if (treeMenuData.length && selectedKeys.length > 0) {
            resetSelectData();
        }
    }, [treeMenuData]);

    const formatTreeData = obj => {
        const treeObj = deepCopy(obj);
        const formatData = (data, parentKey) => {
            for (var i in data) {
                data[i].isEditable = false;
                data[i].value = data[i].name;
                data[i].defaultValue = data[i].name;
                data[i].title = data[i].value;
                if (parentKey) {
                    data[i].parentKey = parentKey;
                }
                if (data[i].children?.length) {
                    formatData(data[i].children, data[i].key);
                } else {
                    data[i].isLeaf = true;
                }
            }
        };
        formatData(treeObj);
        return treeObj;
    };

    const templateManage = useStoreState('templateManage');

    // 更新后。自动选中新增的stage
    const autoSelectAddedStage = NEXT_TREE => {
        if (!newNode) return;
        const effectedTemplate = NEXT_TREE.find(e => e.id === newNode.parentId);
        if (!effectedTemplate) return;
        const expandStageIds = [];
        let responseNewStage;
        for (const item of effectedTemplate.children) {
            expandStageIds.push(item.id);
            if (item.name === newNode.value) {
                responseNewStage = item;
            }
        }
        templateManage.setExpandStageIds(expandStageIds);
        if (!responseNewStage) return;
        const timmer = setTimeout(() => {
            onSelect([responseNewStage.key], { node: responseNewStage });
            setNewNode();
            clearTimeout(timmer);
        });
    };

    const selectedIntoView = () => {
        const selectEl = document.querySelector(
            '.detail-left-tree .ant-tree-treenode-selected'
        );
        selectEl?.scrollIntoView(true);
    };

    const needView = useRef(false);

    const initTreeData = () => {
        if (treeMenuData.length) {
            const NEXT_TREE = formatTreeData(treeMenuData);
            setTreeData(NEXT_TREE);
            autoSelectAddedStage(NEXT_TREE);
        } else {
            setTreeData([]);
        }
        needView.current = true;
    };

    const treeDataChange = () => {
        let key = [treeMenuData[0].key];
        let data = { node: treeMenuData[0] };

        if (treeMenuData[0]?.children?.length) {
            const keys = defaultExpandedKeys
                ? [defaultExpandedKeys]
                : [treeMenuData[0].key];
            setExpandedKeys(keys);
            data.node = treeMenuData[0].children[0];
            data.node.parentKey = treeMenuData[0].key;
            key = [data.node.key];
        }

        if (!autoChangeSelectedKeys) {
            setSelectedKeys(key);
            setSelectedData(treeMenuData[0]);
            onTreeSelect(key, data, treeData, selectedKeys);
        }
        changeAutoChangeSelectedKeys?.(false);
    };

    const resetSelectData = () => {
        const treeList = formatTreeData(treeMenuData);
        const targetData = getTreeTargetData(treeList, selectedKeys[0], 'key');
        if (targetData && (updateType === 'edit' || !updateType)) {
            onTreeSelect(
                selectedKeys,
                { node: targetData },
                treeData,
                selectedKeys
            );
        } else {
            let key = [treeList[0].key];
            if (getResetTreeKey) {
                key = getResetTreeKey?.(treeMenuData, updateType, selectedData);
            }
            const data = getTreeTargetData(treeList, key[0], 'key');
            setSelectedKeys(key);
            onTreeSelect(key, { node: data }, treeData, key);
        }
    };

    // tree 选择回调
    const onSelect = (keys, data) => {
        const { node } = data;
        let key = [node.key];
        if (node.children?.length) {
            const list = deepCopy(expandedKeys);
            if (list.every(item => item !== node.key)) {
                list.push(node.key);
                setExpandedKeys(list);
            }
            key = [node.key, node.children[0].key];
        } else if (node.parentKey) {
            key = [node.key, node.parentKey];
        }
        if (node.key !== selectedKeys[0]) {
            setSelectedKeys(key);
            setSelectedData(node);
            emit('selected-node', { ...node });
        }

        onTreeSelect(keys, data, treeData, selectedKeys);
    };

    const onAdd = (nodeData, isLeaf) => {
        let hierarchy = [...treeData];

        if (beforeAddNode && beforeAddNode({ treeData })) return;
        if (newNodeKey.current) {
            //新增node 之前如果已经有别的新增节点，则需要先删除掉
            deleteNode(newNodeKey.current, hierarchy);
        }
        newNodeKey.current = createUUID();
        let node = {
            value: '',
            defaultValue: '',
            key: newNodeKey.current, // 这个 key 应该是唯一的。 Tip: The key should be unique
            parentKey: nodeData?.key || 0,
            isEditable: false,
            isLeaf,
            is_new: 1,
        };

        if (nodeData) {
            hierarchy.forEach(ele => {
                if (nodeData.key === ele.key) {
                    setExpandedKeys(prev => [
                        ...new Set([...prev, nodeData.key]),
                    ]);
                    node.parentId = ele.id;
                    ele.children.push(node);
                    ele.isLeaf = !isLeaf;
                }
            });
        } else {
            !isLeaf && (node.children = []);
            hierarchy.push(node);
        }

        setTreeData(hierarchy);
        setNewNode(node);
        afterAddNode?.(nodeData, isLeaf, node, hierarchy);
    };
    const changeNode = (key, value, data) =>
        data.forEach(item => {
            if (item.key === key) {
                item.value = value;
            }
            if (item.children) {
                changeNode(key, value, item.children);
            }
        });

    const onChange = (e, key) => {
        e.stopPropagation();
        changeNode(key, e.target.value, treeData);
        setTreeData([...treeData]);
    };

    const removeOutClick = () => {
        handler.current &&
            document.removeEventListener('click', handler.current);
        handler.current = null;
    };

    const closeNode = (key, defaultValue, data) =>
        data.forEach(item => {
            item.isEditable = false;
            if (+item.key === +key) {
                item.value = defaultValue;
            }
            if (item.children) {
                closeNode(key, defaultValue, item.children);
            }
        });

    const onClose = (key, defaultValue, is_new) => {
        removeOutClick();
        selectFlag.current = true;
        newNodeKey.current = undefined;

        setTreeData(prev => {
            const NEXT = [...prev];
            is_new === 1
                ? deleteNode(key, NEXT)
                : closeNode(key, defaultValue, NEXT);
            return NEXT;
        });
    };

    const onSave = async nodeData => {
        const { key, defaultValue, is_new } = nodeData;
        if (verifyValue) {
            if (!verifyValue?.(nodeData.value)) return;
        }
        if (verifySaveValue) {
            if (!verifySaveValue?.(nodeData, treeData)) return;
        }
        removeOutClick();
        selectFlag.current = true;
        setLoading(true);
        try {
            if (is_new) {
                saveNode && (await saveNode(nodeData, treeData));
            } else {
                updateNode && (await updateNode(nodeData));
            }
            onClose(key, defaultValue);
            setTimeout(() => {
                onSelect([key], { node: nodeData });
            });
            setLoading(false);
        } catch (e) {
            onClose(key, defaultValue);
            setLoading(false);
        }
    };

    const clickOutside = (target, key, defaultValue) => {
        if (target) {
            handler.current = event => {
                const targets = Array.isArray(target) ? target : [target];
                if (
                    targets.some(targetItem => {
                        const targetElement = getTargetElement(targetItem);
                        return (
                            !targetElement ||
                            targetElement?.contains(event.target)
                        );
                    })
                ) {
                    return;
                }
                onClose(key, defaultValue, currentNode.current.is_new);
            };

            document.addEventListener('click', handler.current);
        }
    };

    const editNode = (key, data) => {
        data.forEach(item => {
            //   return
            item.isEditable = item.key === key;
            //Tip: Must have, when a node is editable, and you click a button to make other node editable, the node which you don't save yet will be not editable, and its value should be defaultValue
            item.value = item.defaultValue; // 当某节点处于编辑状态，并改变数据，点击编辑其他节点时，此节点变成不可编辑状态，value 需要回退到 defaultvalue
            if (item.children) {
                item.children = editNode(key, item.children);
            }
        });
        return data;
    };

    const onEdit = (key, defaultValue, e) => {
        selectFlag.current = true;
        if (e) {
            e.domEvent.stopPropagation();
        }
        setTreeData(prev => {
            let NEXT = [...prev];
            if (newNodeKey.current && e) {
                //新增的时候，直接去编辑别的节点了，那么先删除新增的节点
                deleteNode(newNodeKey.current, NEXT);
            }
            NEXT = editNode(key, NEXT);
            return NEXT;
        });

        setTimeout(() => {
            clickOutside(wrap.current, key, defaultValue);
        }, 100);
    };

    const deleteNode = (key, data) => {
        data.forEach((item, index) => {
            if (
                currentNode.current.parentKey === item.key &&
                item.children.length === 1
            ) {
                data[index].isLeaf = true;
            }
            if (item.key === key) {
                data.splice(index, 1);
                return;
            } else {
                if (item.children) {
                    deleteNode(key, item.children);
                }
            }
        });
    };

    const onDelete = (nodeData, e) => {
        if (e) e.domEvent.stopPropagation();
        Modal.confirm({
            title: intl.get('base.warning'),
            icon: <ExclamationCircleOutlined />,
            content: '确定要删除吗',
            centered: true,
            okText: intl.get('base.confirm'),
            cancelText: intl.get('base.cancel'),
            wrapClassName: 'delete-tree-node-modal',
            className: 'delete-tree-node-modal',
            async onOk(close) {
                try {
                    if (nodeData.is_new !== 1) {
                        removeNode && (await removeNode(nodeData, treeData));
                    }
                    deleteNode(nodeData.key, treeData);
                    setTreeData([...treeData]);
                    close();
                } catch (e) {
                    close();
                }
            },
        });
    };

    const titleMenu = nodeData => (
        <Menu>
            {!nodeData.isLeaf && (
                <Menu.Item
                    key="0"
                    onClick={() => {
                        onAdd(nodeData, true);
                    }}>
                    {intl.get('templateSettings.addTemplate')}
                </Menu.Item>
            )}
            <Menu.Item
                key="1"
                onClick={e => onEdit(nodeData.key, nodeData.defaultValue, e)}>
                {intl.get('base.rename')}
            </Menu.Item>
            <Menu.Item key="2" onClick={e => onDelete(nodeData, e)}>
                {intl.get('base.delete')}
            </Menu.Item>
        </Menu>
    );

    const handleMore = e => {
        removeOutClick();
        e.stopPropagation();
    };
    const titleAddHandle = () => {
        removeOutClick();
        if (titleAdd) {
            titleAdd();
        } else {
            onAdd(null, true);
        }
    };
    const titleRender = nodeData => {
        if (nodeData.isEditable) {
            return (
                <span
                    onClick={e => e.stopPropagation()}
                    ref={function (span) {
                        if (span !== null) {
                            wrap.current = span;
                        }
                    }}
                    className="edit-wrap"
                    style={{ overflow: 'hidden' }}>
                    <Input
                        ref={function (input) {
                            if (input != null) {
                                if (selectFlag.current) {
                                    selectFlag.current = false;
                                    input.select();
                                }
                                input.focus();
                                currentNode.current = nodeData;
                            }
                        }}
                        maxLength={inputMaxLength}
                        size="mini"
                        className="edit-input"
                        value={nodeData.value}
                        onChange={e => onChange(e, nodeData.key)}
                        onPressEnter={() => onSave(nodeData)}
                    />
                    <SvgIcon
                        iconName="icon-confirm"
                        onClick={e => {
                            e.stopPropagation();
                            nodeData.value !== '' && onSave(nodeData);
                        }}
                        className={`edit-icon ${
                            nodeData.value === '' ? 'disabled-hdc-dom' : ''
                        }`}
                    />
                    <SvgIcon
                        className="edit-icon"
                        iconName="icon-del"
                        onClick={e => {
                            e.stopPropagation();
                            onClose(
                                nodeData.key,
                                nodeData.defaultValue,
                                nodeData.is_new
                            );
                        }}
                    />
                </span>
            );
        } else {
            return (
                <>
                    <div
                        className={`left${
                            Array.isArray(nodeData.children)
                                ? ' is--parent'
                                : ''
                        }`}>
                        <div className="text-wrap">
                            <div className="hide-text">{nodeData.value}</div>
                            <div className={`text-no-wrap title`}>
                                {nodeData.value}
                            </div>
                        </div>
                    </div>

                    {!nodeMenu ? (
                        <Dropdown
                            overlay={
                                customTitleMenu
                                    ? customTitleMenu()
                                    : titleMenu(nodeData)
                            }
                            trigger={['click']}
                            className="directory-dropdown">
                            <i
                                className="icon-more-h filled node__more-menu"
                                onClick={event => handleMore(event)}></i>
                        </Dropdown>
                    ) : (
                        nodeMenu(nodeData)
                    )}
                </>
            );
        }
    };

    return (
        <Spin
            spinning={loading}
            wrapperClassName="left-menu-wrap detail-left-tree">
            <div className="left-tree-menu drawing-directory">
                <div className="header">
                    {menuTitle}
                    {showTitleAdd && (
                        <i
                            className="icon-plus outlined"
                            onClick={titleAddHandle}
                        />
                    )}
                </div>
                <Tree
                    // height={treeHeight}
                    onSelect={async (keys, data) => {
                        if (
                            hasInterceptBeforeChangeNode &&
                            beforeChangeNode &&
                            (await beforeChangeNode({
                                keys,
                                data,
                                callback: onSelect,
                                treeData: treeMenuData,
                            }))
                        ) {
                            return;
                        }
                        onSelect(keys, data);
                    }}
                    multiple={multiple}
                    onExpand={setExpandedKeys}
                    autoExpandParent
                    titleRender={titleRender}
                    selectedKeys={selectedKeys}
                    expandedKeys={expandedKeys}
                    treeData={treeData}
                />
            </div>
        </Spin>
    );
});
export default DetailLeftTree;
